import { useEffect, useRef, useState } from 'react';
import { useOnline } from '../../utilities/online';
import { t } from '../../i18n/i18n';
import './ConnectionStatus.scss';

/** How long "Back online" stays up once the connection returns. */
const BACK_MS = 2600;

/**
 * The one place the whole app says it has lost its connection — every page,
 * signed in or not, mounted once beside the toasts. It reads the browser's
 * own online state (see utilities/online), so it shows the moment the network
 * drops rather than after a request has failed.
 *
 * Nothing to do on the way back: the API refetches every query on screen when
 * the browser reports the connection again (`refetchOnReconnect`, apiSlice),
 * so this only says so.
 */
export default function ConnectionStatus() {
  const online = useOnline();
  // "Back online" is only worth saying after "offline" was — not on load.
  const [back, setBack] = useState(false);
  const wasOffline = useRef(!online);

  useEffect(() => {
    if (!online) {
      wasOffline.current = true;
      setBack(false);
      return undefined;
    }
    if (!wasOffline.current) return undefined;
    wasOffline.current = false;
    setBack(true);
    const timer = setTimeout(() => setBack(false), BACK_MS);
    return () => clearTimeout(timer);
  }, [online]);

  const shown = !online || back;

  return (
    <div
      className={`net-status${shown ? ' is-shown' : ''}${online ? ' is-online' : ''}`}
      role="status"
      aria-live="polite"
    >
      {shown && (
        <>
          <span className="net-status__dot" aria-hidden="true" />
          <span className="net-status__title">{online ? t('Back online') : t('You’re offline')}</span>
          {!online && (
            <span className="net-status__text">
              {t('We’ll reload everything as soon as your connection is back.')}
            </span>
          )}
        </>
      )}
    </div>
  );
}
